import { useParams, Link } from 'react-router-dom'
import { MapPin, Calendar, Award, Globe, ArrowLeft, ExternalLink } from 'lucide-react'
import { useHostProfile } from '../hooks/useAuth'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Avatar } from '../components/ui/Avatar'
import OccurrenceCard from '../components/OccurrenceCard'
import { useIsMobile } from '../hooks/useIsMobile'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'

function formatJoinDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

const DENOM_LABEL: Record<string, string> = Object.fromEntries(
  DENOMINATION_OPTIONS.map((d) => [d.key, d.label])
)

export default function HostProfilePage() {
  const { id } = useParams<{ id: string }>()
  const isMobile = useIsMobile()
  const { data, isLoading, isError } = useHostProfile(id)
  const profile = data?.data

  if (isLoading) {
    return (
      <PublicLayout>
        <div style={{ maxWidth: 640, margin: '80px auto', padding: '0 24px', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
          Loading…
        </div>
      </PublicLayout>
    )
  }

  if (isError || !profile) {
    return (
      <PublicLayout>
        <div style={{ maxWidth: 640, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>Profile not found</div>
          <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 24 }}>
            This profile is private or doesn't exist.
          </div>
          <Link to="/" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
            Browse events
          </Link>
        </div>
      </PublicLayout>
    )
  }

  const denomLabel = profile.denomination ? (DENOM_LABEL[profile.denomination] ?? profile.denomination) : null

  return (
    <PublicLayout>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: isMobile ? '24px 16px 60px' : '40px 40px 80px' }}>

        {/* Back link */}
        <Link
          to="/"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--muted)', textDecoration: 'none', fontSize: 13.5, fontWeight: 500, marginBottom: 28 }}
        >
          <ArrowLeft size={14} strokeWidth={2.2} />
          Browse events
        </Link>

        {/* Profile header */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: 20, padding: isMobile ? '24px 18px' : '32px 28px', marginBottom: 24, boxShadow: '0 1px 3px rgba(31,26,46,.04)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? 14 : 20, marginBottom: 20 }}>
            <Avatar name={profile.displayName} src={profile.avatarUrl} size={isMobile ? 64 : 80} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 26, fontWeight: 700, color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                  {profile.displayName}
                </h1>
                {profile.isCommunityHost && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
                    padding: '3px 9px', borderRadius: 99,
                    background: 'var(--primary-soft)', color: 'var(--primary)',
                    border: '1px solid var(--primary)',
                  }}>
                    <Award size={11} strokeWidth={2.5} />
                    Community Host
                  </span>
                )}
                {profile.isProHost && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                    padding: '3px 8px', borderRadius: 99,
                    background: 'linear-gradient(135deg, #7C5CD3, #3B4CCA)', color: '#fff',
                  }}>
                    PRO
                  </span>
                )}
              </div>

              {/* Meta row */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 8 : 12, fontSize: 13, color: 'var(--muted)' }}>
                {profile.locationLabel && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <MapPin size={13} strokeWidth={2} />
                    {profile.locationLabel}
                  </span>
                )}
                {denomLabel && (
                  <span>{denomLabel}</span>
                )}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Calendar size={13} strokeWidth={2} />
                  Joined {formatJoinDate(profile.joinedAt)}
                </span>
              </div>

              {profile.bio && (
                <p style={{ margin: '12px 0 0', fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55 }}>
                  {profile.bio}
                </p>
              )}
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 0, borderTop: '1px solid var(--line-soft)', padding: '14px 0 0' }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink)' }}>{profile.eventsHostedCount}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500, marginTop: 2 }}>Events hosted</div>
            </div>
          </div>

          {/* Social links (Pro Host only) */}
          {profile.isProHost && (profile.instagramUrl || profile.facebookUrl || profile.websiteUrl) && (
            <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
              {profile.instagramUrl && (
                <a
                  href={profile.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 14px', borderRadius: 99, border: '1.5px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-2)', textDecoration: 'none', fontSize: 13, fontWeight: 500 }}
                >
                  <ExternalLink size={13} strokeWidth={2} />
                  Instagram
                </a>
              )}
              {profile.facebookUrl && (
                <a
                  href={profile.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 14px', borderRadius: 99, border: '1.5px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-2)', textDecoration: 'none', fontSize: 13, fontWeight: 500 }}
                >
                  <ExternalLink size={13} strokeWidth={2} />
                  Facebook
                </a>
              )}
              {profile.websiteUrl && (
                <a
                  href={profile.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 14px', borderRadius: 99, border: '1.5px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-2)', textDecoration: 'none', fontSize: 13, fontWeight: 500 }}
                >
                  <Globe size={14} strokeWidth={2} />
                  Website
                </a>
              )}
            </div>
          )}
        </div>

        {/* Upcoming events */}
        {profile.upcomingEvents.length > 0 && (
          <section>
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>
              Upcoming events
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {profile.upcomingEvents.map((occ) => (
                <OccurrenceCard key={occ.id} occurrence={occ} />
              ))}
            </div>
          </section>
        )}

        {profile.upcomingEvents.length === 0 && profile.eventsHostedCount === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted)', fontSize: 14 }}>
            No upcoming events right now.
          </div>
        )}

        {profile.upcomingEvents.length === 0 && profile.eventsHostedCount > 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted)', fontSize: 14 }}>
            No upcoming events scheduled. Check back soon!
          </div>
        )}
      </div>
    </PublicLayout>
  )
}
